import './Scale.css';
import React, { useState, useEffect } from 'react';
import { Card } from '../../components/Card/Card';
import { loadServerStatus, ServerScaling, ScalingSchedule, deleteSchedule } from '../../model/server';
import { useRemoteData } from '../../model/data';
import { Button } from '../../components/Button/Button';
import { ControlledModal } from '../../components/Modal/ControlledModal';
import { ScheduleForm } from './ScheduleForm';
import { SortableList } from '../../components/SortableList/SortableList';
import { Icon } from '../../components/Icon/Icon';
import { useReloadKey, formatDateTime } from '../../util/util';
import { t } from '../../locales/translate';
import { useConfirmation } from '../../components/ConfirmationPrompt/ConfirmationPrompt';

export function Scale(): JSX.Element {
  const [schedulesKey, reloadSchedules] = useReloadKey();
  const [[status, scalingSchedules], loading, error] = useRemoteData(
    loadServerStatus,
    (db) => {
      return [db.get('serverScalingStatus'), db.table('scalingSchedules').all];
    },
    [schedulesKey.toString()]
  );
  const [openModal, setOpenModal] = useState<'add' | 'edit' | null>(null);

  const [selectedSchedule, setSelectedSchedule] = useState<ScalingSchedule | null>(null);

  async function handleDelete(): Promise<void> {
    if (selectedSchedule) {
      await deleteSchedule(selectedSchedule.id);
      setSelectedSchedule(null);
      reloadSchedules();
    }
  }

  const { confirm } = useConfirmation({
    title: t.model.server.deleteSchedule,
    message: t.model.server.confirmDeleteSchedule,
    action: handleDelete,
    onClose: () => setSelectedSchedule(null),
  });

  useEffect(() => {
    if (selectedSchedule) {
      confirm();
    }
  }, [selectedSchedule]);

  return (
    <Card title={t.model.server.configuration} fullbleed className="server-status">
      <div className={`server-status__status ${status === 'BOOST' ? 'server-status__status--boost' : ''}`}>
        <strong>{t.model.server.status}: </strong>
        <span>{status}</span>
      </div>
      <div className="server-status__schedules-title">
        <strong>{t.model.server.scalingSchedule}</strong>
      </div>
      <div className="server-status__schedules">
        <SortableList
          items={scalingSchedules}
          error={error}
          loading={loading}
          emptyMessage={t.model.server.noSchedules}
          columns={[
            {
              label: t.model.server.serverMode,
              dataKey: 'configuration',
            },
            {
              label: t.model.server.activeOn,
              dataKey: 'on',
              cellRenderer: (row) => formatDateTime(row.on),
            },
            {
              label: t.model.server.actions,
              dataKey: 'id',
              cellRenderer: (row) => {
                return (
                  <Icon
                    name="trash"
                    onClick={() => {
                      setSelectedSchedule(row);
                    }}
                  ></Icon>
                );
              },
            },
          ]}
        />
      </div>
      <footer className="server-status__footer">
        <Button title={t.model.server.createSchedule} type="primary" onClick={() => setOpenModal('add')}></Button>
      </footer>
      <ControlledModal<{
        status: ServerScaling;
        on: Date;
      }>
        open={openModal === 'add'}
        model={{
          status: 'NORMAL',
          on: new Date(),
        }}
        onClose={() => {
          setOpenModal(null);
        }}
        title={t.model.server.createSchedule}
        size="small"
      >
        {(controller) => <ScheduleForm controller={controller} reloadSchedules={reloadSchedules} />}
      </ControlledModal>
    </Card>
  );
}
